import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { WorkAgreementField } from "permissions/resources/work/work-agreement-field";
import { z } from "zod";

export type ProposalAccessJSON = z.infer<typeof ProposalAccessSchema.ProposalAccessJSONZ>;

export type ProposalAccessParams = z.infer<typeof ProposalAccessSchema.ProposalAccessZ>;

export default class ProposalAccessSchema {
  public static readonly ProposalAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.ProposalAccess),
    proposalId: z.uuid().nullish(),
    // `partialRecord`, not `record`: a grant names only the fields it confers, and Zod 4 made
    // `z.record()` with an enum key EXHAUSTIVE — it demands every member. Under `z.record` a narrowed
    // grant failed to parse, which rejected the whole session-permissions request, left
    // `sessionPermissions` undefined, and hung every page gated on it behind a permanent spinner.
    // Partial is the shape the server actually sends; see `proposal-schema.ts`'s `availableActions`,
    // which hit the same Zod 4 change first.
    fields: z.partialRecord(z.enum(WorkAgreementField), z.enum(AccessLevel)).nullish(),
  });

  public static readonly ProposalAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.ProposalAccess),
    proposalId: z.instanceof(Guid).optional(),
    fields: z.partialRecord(z.enum(WorkAgreementField), z.enum(AccessLevel)).optional(),
  });

  public static validate(values: unknown): ProposalAccessParams {
    return parseOrThrow(ProposalAccessSchema.ProposalAccessZ, values, {
      logError: true,
      schemaName: "ProposalAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): ProposalAccessJSON {
    return parseOrThrow(ProposalAccessSchema.ProposalAccessJSONZ, values, {
      logError: true,
      schemaName: "ProposalAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ProposalAccessParams {
    try {
      ProposalAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is ProposalAccessJSON {
    try {
      ProposalAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ProposalAccessJSON): ProposalAccessParams {
    const parsed = parseOrThrow(ProposalAccessSchema.ProposalAccessJSONZ, data, {
      logError: true,
      schemaName: "ProposalAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ProposalAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: ProposalAccessParams): ProposalAccessJSON {
    const parsed = ProposalAccessSchema.validate(notification);
    return ProposalAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ProposalAccessSchema.ProposalAccessJSONZ,
    ProposalAccessSchema.ProposalAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.ProposalAccess as const,
        proposalId: Guid.fromJSON(data.proposalId) ?? undefined,
        fields: (() => {
          if (!data.fields) return;
          let fieldsObj: Record<WorkAgreementField, AccessLevel> = {} as Record<WorkAgreementField, AccessLevel>;
          for (const [key, value] of Object.entries(data.fields ?? {})) {
            // Only accept exact enum member names (and not numeric reverse-mapping keys)
            if (Number.isNaN(Number(key)) && key in WorkAgreementField) {
              const field = WorkAgreementField[key as keyof typeof WorkAgreementField] as unknown as WorkAgreementField;
              const level = AccessLevel[value.toString() as keyof typeof AccessLevel];
              if (level) {
                fieldsObj[field] = value;
              } else {
                console.warn(`Unknown access level in ProposalAccess: ${value}`);
              }
            } else {
              console.warn(`Unknown field in ProposalAccess: ${key}`);
            }
          }
          return fieldsObj;
        })(),
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.ProposalAccess as const,
        proposalId: resource.proposalId?.toJSON(),
        fields: (() => {
          if (!resource.fields) return;
          let fieldsObj: Record<WorkAgreementField, AccessLevel> = {} as Record<WorkAgreementField, AccessLevel>;
          for (const [key, value] of Object.entries(resource.fields)) {
            fieldsObj[key as unknown as WorkAgreementField] = value;
          }
          return fieldsObj;
        })(),
      }),
    }
  );
}
